/**
 * Sanitise an editor-defined anchor id to a safe URL-fragment / id target:
 * strip a leading "#" and any HTML-significant characters so it cannot break
 * out of the `id="..."` attribute. Shared by the Rich Content Block (which
 * sets the id) and PageBlocks (which builds the matching nav href) so the two
 * can never drift (EGDE-76).
 */
export function sanitizeAnchorId(anchorId: string | null | undefined): string {
  return (anchorId ?? "")
    .trim()
    .replace(/^#/, "")
    .replace(/["'<>]/g, "")
}

/**
 * Decodes HTML entities in a string (server-safe version)
 * @param html The HTML string with encoded entities
 * @returns The decoded string
 */
export function decodeHtmlEntities(html: string): string {
  if (!html) return ""

  // Common HTML entities mapping
  const entityMap: Record<string, string> = {
    "&lt;": "<",
    "&gt;": ">",
    "&amp;": "&",
    "&quot;": '"',
    "&#x27;": "'",
    "&#x2F;": "/",
    "&#x60;": "`",
    "&#x3D;": "=",
    "&nbsp;": " ",
  }

  // Replace common entities
  let decoded = html
  Object.entries(entityMap).forEach(([entity, character]) => {
    decoded = decoded.replace(new RegExp(entity, "g"), character)
  })

  // Handle numeric entities like &#39; &#34; etc.
  decoded = decoded.replace(/&#(\d+);/g, (match, dec) => {
    return String.fromCharCode(dec)
  })

  // Handle hex entities like &#x27; &#x22; etc.
  decoded = decoded.replace(/&#x([0-9a-f]+);/gi, (match, hex) => {
    return String.fromCharCode(parseInt(hex, 16))
  })

  return decoded
}

/**
 * Strips HTML tags from a string
 * @param html The HTML string
 * @returns The string with HTML tags removed
 */
export function stripHtmlTags(html: string): string {
  if (!html) return ""
  return html.replace(/<[^>]*>/g, "")
}

/**
 * Cleans HTML content for use in meta descriptions:
 * 1. Decodes HTML entities
 * 2. Replaces block elements with spaces/newlines
 * 3. Strips remaining HTML tags
 * 4. Normalizes whitespace
 * 5. Truncates to specified length
 * @param html The HTML string to clean
 * @param maxLength Maximum length of the result (default: 160)
 * @returns Clean text suitable for meta descriptions
 */
export function cleanHtmlForMeta(
  html: string,
  maxLength: number = 160,
): string {
  if (!html) return ""

  // First decode HTML entities
  let cleaned = decodeHtmlEntities(html)

  // Replace block elements with spaces to avoid words running together
  cleaned = cleaned
    .replace(/<\/?(div|p|br|li|ul|ol|h[1-6])[^>]*>/gi, " ")
    .replace(/<\/?(tr|td|th)[^>]*>/gi, " ")

  // Strip all remaining HTML tags
  cleaned = stripHtmlTags(cleaned)

  // Normalize whitespace
  cleaned = cleaned.replace(/\s+/g, " ").trim()

  // Truncate if necessary
  if (cleaned.length > maxLength) {
    cleaned = cleaned.substring(0, maxLength - 3) + "..."
  }

  return cleaned
}
